import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import PageTransition from './PageTransition';
import { ThemeToggleCompact } from './ThemeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Dialog, DialogContent } from './ui/dialog';
import { 
  LayoutDashboard, 
  Calculator, 
  History, 
  LogOut, 
  User, 
  Menu,
  Globe,
  TrendingUp,
  TrendingDown,
  Target,
  Play,
  Bell,
  BarChart3,
  BookOpen,
  Newspaper,
  PiggyBank,
  Shield,
  Scale,
  Flame,
  ChevronRight,
  CheckCircle,
  Clock,
  Percent,
  ArrowRight,
  RefreshCw,
  CreditCard,
  HelpCircle,
  Mail,
  Crown,
  Settings,
  Zap
} from 'lucide-react';
import InfoGuidePanel, { MarketHoursDisplay } from './InfoGuide';
import NotificationDropdown from './NotificationDropdown';
import Footer from './Footer';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Risk Profile Questionnaire Questions
const QUESTIONS = [
  {
    id: 1,
    question: "Quel est votre niveau d'expérience en trading ?",
    questionEn: "What is your trading experience level?",
    icon: User,
    options: [
      { label: "Débutant (< 6 mois)", labelEn: "Beginner (< 6 months)", score: 1 },
      { label: "Intermédiaire (6 mois - 2 ans)", labelEn: "Intermediate (6 months - 2 years)", score: 2 },
      { label: "Confirmé (2 - 5 ans)", labelEn: "Experienced (2 - 5 years)", score: 3 },
      { label: "Expert (> 5 ans)", labelEn: "Expert (> 5 years)", score: 4 },
    ]
  },
  {
    id: 2,
    question: "Face à un krach de -40% sur le marché, quelle est votre réaction ?",
    questionEn: "Facing a -40% market crash, what's your reaction?",
    icon: TrendingDown,
    options: [
      { label: "Je suis très anxieux, je préfère sécuriser", labelEn: "Very anxious, I prefer to secure", score: 1 },
      { label: "Je suis inquiet mais je ne touche à rien", labelEn: "Worried but I don't touch anything", score: 2 },
      { label: "C'est normal, je reste sur ma stratégie", labelEn: "It's normal, I stick to my strategy", score: 3 },
      { label: "Excellent ! C'est le moment d'acheter", labelEn: "Excellent! Time to buy more", score: 4 },
    ]
  },
  {
    id: 3,
    question: "Quel pourcentage de votre capital êtes-vous prêt à risquer par trade ?",
    questionEn: "What percentage of your capital are you willing to risk per trade?",
    icon: Percent,
    options: [
      { label: "0.5% - 1% (très conservateur)", labelEn: "0.5% - 1% (very conservative)", score: 1 },
      { label: "1% - 2% (modéré)", labelEn: "1% - 2% (moderate)", score: 2 },
      { label: "2% - 5% (agressif)", labelEn: "2% - 5% (aggressive)", score: 3 },
      { label: "> 5% (très agressif)", labelEn: "> 5% (very aggressive)", score: 4 },
    ]
  },
  {
    id: 4,
    question: "Quel est votre style de trading préféré ?",
    questionEn: "What is your preferred trading style?",
    icon: Clock,
    options: [
      { label: "Scalping (minutes)", labelEn: "Scalping (minutes)", score: 1 },
      { label: "Day trading (heures)", labelEn: "Day trading (hours)", score: 2 },
      { label: "Swing trading (jours/semaines)", labelEn: "Swing trading (days/weeks)", score: 3 },
      { label: "Position trading (semaines/mois)", labelEn: "Position trading (weeks/months)", score: 4 },
    ]
  },
  {
    id: 5,
    question: "Quel est votre objectif principal ?",
    questionEn: "What is your main objective?",
    icon: Target,
    options: [
      { label: "Préserver mon capital avec des gains réguliers", labelEn: "Preserve capital with steady gains", score: 1 },
      { label: "Croissance équilibrée avec risque modéré", labelEn: "Balanced growth with moderate risk", score: 2 },
      { label: "Maximiser les gains, même avec plus de risque", labelEn: "Maximize gains, even with more risk", score: 3 },
      { label: "Gains explosifs, je suis prêt à tout perdre", labelEn: "Explosive gains, ready to lose it all", score: 4 },
    ]
  }
];

const PROFILES = {
  prudent: { name: "Prudent", nameEn: "Prudent", description: "Levier 10x, TP proche", icon: Shield, color: "text-green-400", bgColor: "bg-green-500/20", leverage: "10x" },
  balanced: { name: "Équilibré", nameEn: "Balanced", description: "Levier 20x, TP moyen", icon: Scale, color: "text-yellow-400", bgColor: "bg-yellow-500/20", leverage: "20x" },
  dynamic: { name: "Dynamique", nameEn: "Dynamic", description: "Levier 30x, TP éloigné", icon: Flame, color: "text-red-400", bgColor: "bg-red-500/20", leverage: "30x" }
};

const Layout = ({ children }) => {
  const { user, logout, isAuthenticated, updateRiskProfile, token } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  
  // Questionnaire state
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [profile, setProfile] = useState(null);
  
  const handleStartQuestionnaire = () => {
    setShowQuestionnaire(true);
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setProfile(null);
  };
  
  const handleAnswer = (score) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);
    
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const totalScore = newAnswers.reduce((a, b) => a + b, 0);
      let profileKey;
      if (totalScore <= 8) profileKey = 'prudent';
      else if (totalScore <= 14) profileKey = 'balanced';
      else profileKey = 'dynamic';
      
      setProfile(profileKey);
      setShowResult(true);
    }
  };
  
  const handleSaveProfile = async () => {
    if (profile && isAuthenticated) {
      const totalScore = answers.reduce((a, b) => a + b, 0);
      await updateRiskProfile(profile, totalScore);
    }
    setShowQuestionnaire(false);
  };

  // Fetch unread notifications count
  useEffect(() => {
    if (isAuthenticated && token) {
      const fetchUnreadCount = async () => {
        try {
          const response = await axios.get(`${API}/notifications/unread-count`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUnreadAlerts(response.data.unread_count || 0);
        } catch (error) {
          // Silently fail
        }
      };
      fetchUnreadCount();
      // Poll every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, token]);

  // Trade & DCA monitoring - runs every 20 minutes
  useEffect(() => {
    if (isAuthenticated && token) {
      const runMonitoring = async () => {
        try {
          // Call all monitoring endpoints
          await Promise.all([
            axios.post(`${API}/monitoring/trades`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.post(`${API}/monitoring/dca`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.post(`${API}/economic-calendar/check-results`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);
          console.log('[Monitoring] Check completed');
        } catch (error) {
          // Silently fail - user will see notifications anyway
          console.log('[Monitoring] Check failed:', error.message);
        }
      };
      
      // Run immediately on login, then every 20 minutes
      runMonitoring();
      const monitoringInterval = setInterval(runMonitoring, 20 * 60 * 1000);
      
      return () => clearInterval(monitoringInterval);
    }
  }, [isAuthenticated, token]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Navigation items - SAME for all users (connected or not)
  const navItems = [
    { path: '/dashboard', label: language === 'fr' ? 'Mes Marchés' : 'My Markets', icon: LayoutDashboard },
    { path: '/news-eco', label: language === 'fr' ? 'News Éco' : 'Eco News', icon: Newspaper },
    { path: '/analysis', label: language === 'fr' ? 'Analyse IA' : 'AI Analysis', icon: Target },
    { path: '/scan-auto', label: 'Scan Auto', icon: Zap },
    { path: '/dca', label: 'DCA', icon: PiggyBank },
    { path: '/learn', label: language === 'fr' ? 'Formation' : 'Learn', icon: BookOpen },
    { path: '/calculator', label: t('nav.calculator'), icon: Calculator },
    { path: '/history', label: language === 'fr' ? 'Journal' : 'Journal', icon: History },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header with safe area for PWA/notch */}
      <header className="sticky top-0 z-50 glass border-b border-white/5 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo - Simple text */}
            <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
              <span className="font-bold text-xl tracking-tight">
                TRADEX<span className="text-primary"> AI</span>
              </span>
            </Link>

            {/* Desktop Nav - Always visible */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <ThemeToggleCompact />

              {/* Language Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleLanguage}
                data-testid="language-toggle"
                className="text-muted-foreground hover:text-foreground"
              >
                <Globe className="w-5 h-5" />
                <span className="sr-only">Toggle language</span>
              </Button>
              <span className="text-xs font-mono text-muted-foreground uppercase">
                {language}
              </span>

              {isAuthenticated ? (
                <>
                  {/* Notifications Dropdown */}
                  <NotificationDropdown />

                  {/* Mobile Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild className="md:hidden">
                      <Button variant="ghost" size="icon" data-testid="mobile-menu">
                        <Menu className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-card border-white/10">
                      {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem key={item.path} asChild>
                            <Link to={item.path} className="flex items-center gap-2" data-testid={`mobile-nav-${item.path.slice(1)}`}>
                              <Icon className="w-4 h-4" />
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Info Guide Button */}
                  <InfoGuidePanel>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-muted-foreground hover:text-primary"
                      data-testid="info-guide-btn"
                    >
                      <HelpCircle className="w-5 h-5" />
                    </Button>
                  </InfoGuidePanel>

                  {/* User Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="gap-2" data-testid="user-menu">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center relative ${user?.is_founder ? 'bg-gradient-to-br from-amber-400/30 to-amber-600/20 ring-1 ring-amber-400/50' : 'bg-primary/20'}`}>
                          <User className={`w-4 h-4 ${user?.is_founder ? 'text-amber-400' : 'text-primary'}`} />
                          {user?.is_founder && (
                            <Crown className="w-3 h-3 text-amber-400 absolute -top-1 -right-1 drop-shadow-[0_0_4px_rgba(251,191,36,0.8)]" data-testid="founder-crown-icon" />
                          )}
                        </div>
                        <span className="hidden sm:flex items-center gap-1.5 text-sm font-medium">
                          {user?.name?.split(' ')[0]}
                          {user?.is_founder && (
                            <span
                              data-testid="founder-badge"
                              className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-gradient-to-r from-amber-500/20 to-amber-600/10 text-amber-400 border border-amber-400/30"
                            >
                              Fondateur
                            </span>
                          )}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-card border-white/10">
                      {/* Founder badge in dropdown */}
                      {user?.is_founder && (
                        <>
                          <div className="px-2 py-2 flex items-center gap-2 bg-gradient-to-r from-amber-500/10 to-transparent rounded-md" data-testid="founder-menu-header">
                            <Crown className="w-4 h-4 text-amber-400" />
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
                                Programme Fondateur
                              </div>
                              <div className="text-[11px] text-muted-foreground">
                                Accès à vie · Bêta prioritaire
                              </div>
                            </div>
                          </div>
                          <DropdownMenuSeparator />
                        </>
                      )}

                      {/* Current Profile Display */}
                      {user?.risk_profile && (
                        <>
                          <div className="px-2 py-1.5">
                            <div className="text-xs text-muted-foreground mb-1">
                              {language === 'fr' ? 'Mon profil de risque' : 'My risk profile'}
                            </div>
                            <div className={`flex items-center gap-2 ${PROFILES[user.risk_profile]?.color || 'text-foreground'}`}>
                              {PROFILES[user.risk_profile] && (
                                <>
                                  {React.createElement(PROFILES[user.risk_profile].icon, { className: 'w-4 h-4' })}
                                  <span className="font-medium">
                                    {language === 'fr' ? PROFILES[user.risk_profile].name : PROFILES[user.risk_profile].nameEn}
                                  </span>
                                  <span className="text-xs opacity-75">({PROFILES[user.risk_profile].leverage})</span>
                                </>
                              )}
                            </div>
                          </div>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      
                      {/* Redo Questionnaire */}
                      <DropdownMenuItem 
                        onClick={handleStartQuestionnaire}
                        data-testid="redo-questionnaire-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Évaluer mon profil' : 'Evaluate my profile'}
                      </DropdownMenuItem>
                      
                      {/* Scan Auto Settings */}
                      <DropdownMenuItem asChild>
                        <Link to="/scan-auto">
                          <Zap className="w-4 h-4 mr-2" />
                          {language === 'fr' ? 'Scan Auto' : 'Auto Scan'}
                        </Link>
                      </DropdownMenuItem>
                      
                      {/* Subscribe / Upgrade */}
                      <DropdownMenuItem asChild>
                        <Link to="/subscribe">
                          <CreditCard className="w-4 h-4 mr-2" />
                          {language === 'fr' ? 'Abonnement' : 'Subscription'}
                        </Link>
                      </DropdownMenuItem>

                      {/* Account settings (profile, security, subscription, emails) */}
                      <DropdownMenuItem asChild>
                        <Link to="/account/settings" data-testid="account-settings-nav-link">
                          <Settings className="w-4 h-4 mr-2" />
                          {language === 'fr' ? 'Paramètres du compte' : 'Account settings'}
                        </Link>
                      </DropdownMenuItem>

                      {user?.is_admin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link to="/admin" data-testid="admin-nav-link" className="cursor-pointer flex items-center text-amber-400 focus:text-amber-400">
                              <Shield className="w-4 h-4 mr-2" />
                              Dashboard Admin
                            </Link>
                          </DropdownMenuItem>
                        </>
                      )}

                      <DropdownMenuSeparator />

                      <DropdownMenuItem 
                        onClick={handleLogout}
                        className="text-destructive focus:text-destructive"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('nav.logout')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  {/* Mobile Menu for non-authenticated */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild className="md:hidden">
                      <Button variant="ghost" size="icon" data-testid="mobile-menu-public">
                        <Menu className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-card border-white/10">
                      {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem key={item.path} asChild>
                            <Link to={item.path} className="flex items-center gap-2" data-testid={`mobile-nav-${item.path.slice(1)}`}>
                              <Icon className="w-4 h-4" />
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  <Link to="/login">
                    <Button variant="ghost" data-testid="login-btn">
                      {t('nav.login')}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button data-testid="register-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
                      {t('nav.register')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Page Transition */}
      <main>
        <PageTransition>
          {children}
        </PageTransition>
      </main>

      {/* Global footer with support & legal links */}
      <Footer />
      
      {/* Risk Profile Questionnaire Dialog */}
      <Dialog open={showQuestionnaire} onOpenChange={setShowQuestionnaire}>
        <DialogContent className="max-w-lg bg-[#0d0d1a] border-white/10">
          {!showResult ? (
            <>
              {/* Question */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Question {currentQuestion + 1}/{QUESTIONS.length}
                  </span>
                  <div className="flex gap-1">
                    {QUESTIONS.map((_, idx) => (
                      <div 
                        key={idx} 
                        className={`w-2 h-2 rounded-full ${idx <= currentQuestion ? 'bg-primary' : 'bg-white/20'}`} 
                      />
                    ))}
                  </div>
                </div>
                
                <div className="text-center space-y-4">
                  {React.createElement(QUESTIONS[currentQuestion].icon, { 
                    className: 'w-12 h-12 mx-auto text-primary' 
                  })}
                  <h3 className="text-xl font-bold">
                    {language === 'fr' 
                      ? QUESTIONS[currentQuestion].question 
                      : QUESTIONS[currentQuestion].questionEn}
                  </h3>
                </div>
                
                <div className="space-y-3">
                  {QUESTIONS[currentQuestion].options.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(option.score)}
                      className="w-full p-4 rounded-lg border border-white/10 hover:border-primary/50 hover:bg-primary/5 text-left transition-all flex items-center justify-between group"
                    >
                      <span>{language === 'fr' ? option.label : option.labelEn}</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Result */}
              <div className="text-center space-y-6 py-4">
                <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${PROFILES[profile]?.bgColor}`}>
                  {PROFILES[profile] && React.createElement(PROFILES[profile].icon, { 
                    className: `w-10 h-10 ${PROFILES[profile].color}` 
                  })}
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold mb-2">
                    {language === 'fr' ? 'Votre profil :' : 'Your profile:'}
                  </h3>
                  <p className={`text-3xl font-bold ${PROFILES[profile]?.color}`}>
                    {language === 'fr' ? PROFILES[profile]?.name : PROFILES[profile]?.nameEn}
                  </p>
                  <p className="text-muted-foreground mt-2">
                    {PROFILES[profile]?.description}
                  </p>
                </div>
                
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  {language === 'fr' 
                    ? 'Ce profil sera utilisé pour vos analyses' 
                    : 'This profile will be used for your analyses'}
                </div>
                
                <button
                  onClick={handleSaveProfile}
                  className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  {language === 'fr' ? 'Enregistrer mon profil' : 'Save my profile'}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
